import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

interface VerificationStatus {
  user_id: string;
  email_verified: boolean;
  whatsapp_verified: boolean;
  account_activated: boolean;
  email_verified_at?: string;
  whatsapp_verified_at?: string;
}

const VerificationForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus | null>(null);
  const [emailCode, setEmailCode] = useState("");
  const [whatsappCode, setWhatsappCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchVerificationStatus();
    }
  }, [user]);

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get("/api/verification/status");
      setVerificationStatus(response.data);
    } catch (error: any) {
      console.error("Failed to fetch verification status:", error);
      setError("Failed to load verification status");
    }
  };

  const handleVerifyCode = async (type: "email" | "whatsapp") => {
    if (!user) return;

    const code = type === "email" ? emailCode : whatsappCode;
    if (!code.trim()) {
      setError(`Please enter the ${type} verification code`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post("/api/verification/verify-code", {
        user_id: user.userId,
        verification_type: type,
        code: code.trim(),
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        if (response.data.account_activated) {
          setSuccess("Account activated successfully! Redirecting...");
          setTimeout(() => {
            navigate("/");
          }, 2000);
        }
        // Refresh verification status
        await fetchVerificationStatus();
        // Clear the code input
        if (type === "email") {
          setEmailCode("");
        } else {
          setWhatsappCode("");
        }
      }
    } catch (error: any) {
      console.error(`Failed to verify ${type} code:`, error);
      setError(error.response?.data?.detail || `Failed to verify ${type} code`);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async (type: "email" | "whatsapp") => {
    if (!user) return;

    setResending(type);
    setError(null);

    try {
      const response = await api.post(
        `/api/verification/resend-code?verification_type=${type}`
      );
      if (response.data.success) {
        setSuccess(response.data.message);
      }
    } catch (error: any) {
      console.error(`Failed to resend ${type} code:`, error);
      setError(error.response?.data?.detail || `Failed to resend ${type} code`);
    } finally {
      setResending(null);
    }
  };

  const getActiveStep = () => {
    if (!verificationStatus) return 0;
    if (
      !verificationStatus.email_verified &&
      !verificationStatus.whatsapp_verified
    )
      return 0;
    if (
      verificationStatus.email_verified &&
      !verificationStatus.whatsapp_verified
    )
      return 1;
    if (
      verificationStatus.email_verified &&
      verificationStatus.whatsapp_verified
    )
      return 2;
    return 2;
  };

  if (!user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography>Please log in to access verification.</Typography>
      </Box>
    );
  }

  if (!verificationStatus) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (verificationStatus.account_activated) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Card sx={{ maxWidth: 500, textAlign: "center" }}>
          <CardContent>
            <Typography variant="h5" color="success.main" gutterBottom>
              ✅ Account Verified
            </Typography>
            <Typography variant="body1" paragraph>
              Your account has been successfully verified and activated.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/")}
              fullWidth
            >
              Continue to Dashboard
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Card sx={{ maxWidth: 600, width: "100%" }}>
        <CardContent>
          <Typography variant="h4" align="center" gutterBottom>
            Account Verification
          </Typography>

          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            paragraph
          >
            Please verify your contact information to activate your account
          </Typography>

          {/* Progress Stepper */}
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={getActiveStep()} alternativeLabel>
              <Step>
                <StepLabel>Email Verification</StepLabel>
              </Step>
              <Step>
                <StepLabel>WhatsApp Verification</StepLabel>
              </Step>
              <Step>
                <StepLabel>Account Activated</StepLabel>
              </Step>
            </Stepper>
          </Box>

          {/* Status Chips */}
          <Box display="flex" gap={2} justifyContent="center" mb={3}>
            <Chip
              label={`Email ${
                verificationStatus.email_verified ? "✓ Verified" : "⏳ Pending"
              }`}
              color={verificationStatus.email_verified ? "success" : "warning"}
              variant={
                verificationStatus.email_verified ? "filled" : "outlined"
              }
            />
            <Chip
              label={`WhatsApp ${
                verificationStatus.whatsapp_verified
                  ? "✓ Verified"
                  : "⏳ Pending"
              }`}
              color={
                verificationStatus.whatsapp_verified ? "success" : "warning"
              }
              variant={
                verificationStatus.whatsapp_verified ? "filled" : "outlined"
              }
            />
          </Box>

          {/* Error/Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Email Verification */}
          {!verificationStatus.email_verified && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                📧 Email Verification
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Check your email for a verification code and enter it below.
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  label="Email Verification Code"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1 }}
                  placeholder="Enter 6-digit code"
                />
                <Button
                  variant="outlined"
                  onClick={() => handleVerifyCode("email")}
                  disabled={loading || !emailCode.trim()}
                >
                  {loading ? <CircularProgress size={20} /> : "Verify"}
                </Button>
                <Button
                  variant="text"
                  onClick={() => handleResendCode("email")}
                  disabled={resending === "email"}
                  size="small"
                >
                  {resending === "email" ? (
                    <CircularProgress size={16} />
                  ) : (
                    "Resend"
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* WhatsApp Verification */}
          {!verificationStatus.whatsapp_verified && (
            <Box mb={3}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                📱 WhatsApp Verification
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Check your WhatsApp for a verification code and enter it below.
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  label="WhatsApp Verification Code"
                  value={whatsappCode}
                  onChange={(e) => setWhatsappCode(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1 }}
                  placeholder="Enter 6-digit code"
                />
                <Button
                  variant="outlined"
                  onClick={() => handleVerifyCode("whatsapp")}
                  disabled={loading || !whatsappCode.trim()}
                >
                  {loading ? <CircularProgress size={20} /> : "Verify"}
                </Button>
                <Button
                  variant="text"
                  onClick={() => handleResendCode("whatsapp")}
                  disabled={resending === "whatsapp"}
                  size="small"
                >
                  {resending === "whatsapp" ? (
                    <CircularProgress size={16} />
                  ) : (
                    "Resend"
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* Help Text */}
          <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Need help?</strong>
              <br />
              • Check your spam/junk folder for the email
              <br />
              • Make sure your WhatsApp number is correct
              <br />
              • Codes expire after 24 hours
              <br />• Contact support if you continue having issues
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerificationForm;
